/**
 * Etiquetas en español para el enum `alert_type` (#566).
 *
 * La tarjeta de `/alertas` pintaba `alert_type.replace(/_/g, " ")` en
 * mayúsculas, o sea el nombre interno del enum en inglés
 * ("EXPEDIENTE CHECKLIST PENDING", "OPPORTUNITY CLOSING SOON"). El
 * secretario de planeación no habla el esquema de la base de datos, y
 * el producto va todo en español (CLAUDE.md, principio 9).
 *
 * **Fuente de verdad del enum:** Postgres, tipo `alert_type`, armado
 * entre tres migraciones:
 *  - `0001_initial_schema` — los 9 originales.
 *  - `0017_alerts_v2_schema` — +5 (Alertas v2).
 *  - `0031_nudges_expedientes_alert_types` — +3 (nudges de expedientes).
 *
 * **Al agregar un valor nuevo en el backend:** súmalo a `ALERT_TYPES` y
 * dale su etiqueta acá. `ALERT_TYPE_LABELS` es un `Record<AlertType,
 * string>`, así que el typecheck del frontend falla hasta que tenga
 * copy. Mientras tanto la UI muestra `ALERT_TYPE_FALLBACK_LABEL` — nunca
 * el enum crudo.
 */

/** Los 17 valores del enum `alert_type` de Postgres. */
export const ALERT_TYPES = [
  // --- 0001: iniciales ---
  "new_matching_opportunity",
  "opportunity_closing_soon",
  "project_status_changed",
  "subsanation_required",
  "invoice_generated",
  "commission_billable",
  "scraper_failed",
  "municipality_at_risk",
  "high_value_opportunity_for_reactivation",
  // --- 0017: Alertas v2 ---
  "peer_won_grant",
  "score_dropped",
  "sector_opportunity_burst",
  "draft_aging",
  "deadline_cluster",
  // --- 0031: nudges de expedientes ---
  "expediente_deadline_soon",
  "expediente_checklist_pending",
  "expediente_stale",
] as const;

export type AlertType = (typeof ALERT_TYPES)[number];

/**
 * Etiqueta corta por tipo. La tarjeta las pinta en mayúsculas por CSS,
 * así que van cortas (máximo 3 palabras) para no romper la fila de
 * badges en pantallas angostas.
 */
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  new_matching_opportunity: "Convocatoria nueva",
  opportunity_closing_soon: "Cierra pronto",
  project_status_changed: "Cambio de estado",
  subsanation_required: "Subsanación",
  invoice_generated: "Factura emitida",
  commission_billable: "Comisión facturable",
  scraper_failed: "Fuente caída",
  municipality_at_risk: "Municipio en riesgo",
  high_value_opportunity_for_reactivation: "Oportunidad de alto valor",
  peer_won_grant: "Un vecino captó",
  score_dropped: "Score a la baja",
  sector_opportunity_burst: "Racha en tu sector",
  draft_aging: "Borrador sin avance",
  deadline_cluster: "Cierres agrupados",
  expediente_deadline_soon: "Expediente por vencer",
  expediente_checklist_pending: "Checklist pendiente",
  expediente_stale: "Expediente sin movimiento",
};

/**
 * Qué mostrar cuando el backend manda un tipo que este build no conoce
 * (migración desplegada antes que el frontend). Genérico a propósito:
 * decir "Alerta" no informa mucho, pero es infinitamente mejor que
 * filtrar el nombre del enum a la pantalla del cliente.
 */
export const ALERT_TYPE_FALLBACK_LABEL = "Alerta";

function isKnownAlertType(alertType: string): alertType is AlertType {
  return Object.prototype.hasOwnProperty.call(ALERT_TYPE_LABELS, alertType);
}

/** Etiqueta en español para un `alert_type`. Nunca devuelve el enum crudo. */
export function alertTypeLabel(alertType: string): string {
  return isKnownAlertType(alertType) ? ALERT_TYPE_LABELS[alertType] : ALERT_TYPE_FALLBACK_LABEL;
}
